import {
  getDisplayReasons,
  type CancelSubscriptionReasonId,
} from "./cancel-reasons";
import { getDelayPresets } from "./pause-dates";
import { type RetentionOffer } from "./retention-offers";
import {
  contractOneTimeTotal,
  contractPerDeliveryTotal,
} from "./subscription-pricing";
import type { ManageSubscriptionSheetProduct } from "./types";
import {
  leversForSubReason,
  type CancelSubReasonId,
} from "shared/cancel-sub-reason";

/**
 * Every retention lever the AI-driven chat may pull — the RetentionOffer set plus
 * swap and switch-to-one-time. Mirrors `EligibleOffer` in the server `prompt.ts`.
 */
export type EligibleOffer =
  | RetentionOffer
  | "swap"
  | "switch_to_one_time"
  // Computed size-up bundle (Rose Nectar, single-line, price roots). Added to the
  // menu only when the widget has actually computed a qualifying bundle; the
  // sub-reason `leads` make it the lead price save when present. See
  // cancel-chat-bundle.ts and the server prompt's eligibleOfferSchema (keep in sync).
  | "bundle";

/**
 * The decision context handed to the cancellation chatbot backend, built here on
 * the widget so reasons, prices and delay dates stay deterministic — the model
 * presents these, it never authors them.
 *
 * It does NOT decide which lever to pull, and it no longer lets the customer's
 * REASON decide either: `eligibleOffersByReason` is availability, not editorial.
 * The legacy step dialog (the control arm) still walks a reason-specific sequence
 * via `buildRetentionOfferSequence`. The chat deliberately does not.
 *
 * Mirrors `cancelChatContextSchema` in `src/lib/cancel-chat/prompt.ts` (server).
 * Keep the two in sync.
 */
export interface CancelChatContext {
  productTitle: string;
  isBeforeSecondShipment: boolean;
  isFlexible: boolean;
  currencySymbol: string;
  /**
   * What they pay today for the conversion scope: whole-contract total on the
   * cancel-everything path, or the chosen line under singleLineScope.
   */
  paidAmount: number | null;
  /** Matching one-time (non-subscription) total for the same scope. */
  oneTimeAmount: number | null;
  /** max(0, oneTime - paid) — the extra a one-time purchase costs. */
  estimatedDiff: number | null;
  /**
   * The fee card offers "Save $X — cancel after my next delivery": switch on AND
   * estimatedDiff > 0 AND a next billing date. Unlocks the server's
   * accept_cancel_after_next_delivery tool.
   */
  cancelAfterNextDeliveryAvailable: boolean;
  allowedReasons: { id: CancelSubscriptionReasonId; label: string }[];
  delayPresets: { weeks: number; date: string }[];
  changeFrequencyAvailable: boolean;
  /** Current billing cadence (normalized lowercase unit), or null when unknown. */
  cadence: { interval: number; intervalUnit: string } | null;
  nextBillingDate: string | null;
  /** Deliveries received so far (tenure). */
  orderCount: number | null;
  customerFirstName: string | null;
  /** Other products on the SAME subscription contract. */
  siblingLineTitles: string[];
  /**
   * Qualifying swap-bundle candidates (website-available, never bought,
   * priced variants) the model may compose offer_swap's productIds from.
   * `id` is the selection key (variant id when known). The widget re-validates
   * any composition against the value rules.
   */
  swapCandidates: { id: number; title: string; price: number }[];
  /**
   * What the WHOLE subscription costs per delivery — the same figure the manage
   * sheet shows. Spacing deliveries out re-cadences every line on the contract,
   * so the saving we quote has to describe every line, not just the one the
   * customer clicked.
   */
  contractPerDeliveryTotal: number | null;
  /**
   * Eligible retention levers per reason id, in RECOMMENDED order (the AI treats
   * this as a hint, not a forced sequence). Business rules are encoded here as
   * *which levers are eligible*: before the 2nd shipment excludes swap;
   * flexible excludes switch_to_one_time.
   * Swap is listed optimistically — the widget drops it per-turn when there aren't
   * two swap candidates to show.
   */
  eligibleOffersByReason: Record<string, EligibleOffer[]>;
  /**
   * Set when the conversation is scoped to ONE line of a multi-line contract: the
   * product being removed and the lines that keep shipping. It rescopes
   * `productTitle` to the chosen line, empties `siblingLineTitles` (the siblings
   * are NOT being cancelled), and rides to the server so the prompt reassures
   * rather than announcing a cancellation. Null on the whole-contract flow
   * (single-line subs and the "cancel everything" branch).
   */
  singleLineScope: { removedTitle: string; retainedTitles: string[] } | null;
}

/** Shared eligibility menu — every preset reason gets the same `stateOffers`. */
export function getEligibleOffersForReason(
  eligibleOffersByReason: Record<string, EligibleOffer[]>,
  reasonId: string,
): EligibleOffer[] {
  return (
    eligibleOffersByReason[reasonId] ??
    Object.values(eligibleOffersByReason)[0] ??
    []
  );
}

/**
 * Narrow the per-turn menu to levers that actually answer the recorded root.
 * When there is no sub-reason (or it names no leads), keep the full eligibility
 * menu — fit-steering is unavailable, so we must not empty the table.
 */
export function filterOffersToSubReasonLeads(
  offers: readonly EligibleOffer[],
  subReason: CancelSubReasonId | null | undefined,
): EligibleOffer[] {
  const leads = leversForSubReason(subReason);
  if (leads.length === 0) return [...offers];
  const leadSet = new Set<string>(leads);
  return offers.filter((o) => leadSet.has(o));
}

/**
 * Roots where an empty fitting set means "do not pitch a mismatched save" —
 * unlock confirm instead (e.g. dislike without swap), even before any decline.
 * Other roots may fall back to the eligible menu for a first save attempt, but
 * never after an offer was already declined (confirm is unlocked then).
 */
export const SKIP_SAVE_WHEN_NO_FITTING_LEVER: ReadonlySet<CancelSubReasonId> =
  new Set(["dislike_product"]);

/**
 * Per-turn offer menu after eligibility filters + sub-reason fit policy.
 *
 * Prefer levers that answer the recorded root. When none remain:
 * - pre-2nd → closest eligible (cancel unavailable)
 * - dislike (and other SKIP_SAVE roots) → empty (unlock confirm)
 * - after any offer decline → empty (confirm already unlocked; do not pitch a mismatch)
 * - otherwise → full eligible menu for one first save attempt
 *
 * Pre-2nd always keeps `switch_to_one_time` when eligible — it is the real exit
 * (cancel substitute), so fit-gating must not strip it. It stays LAST in the
 * menu, never led with.
 */
export function resolveAvailableOffersForSubReason(args: {
  eligible: readonly EligibleOffer[];
  subReason: CancelSubReasonId | null | undefined;
  isBeforeSecondShipment: boolean;
  /** True once the customer has declined any retention offer this conversation. */
  offerDeclined?: boolean;
}): EligibleOffer[] {
  const fitting = filterOffersToSubReasonLeads(args.eligible, args.subReason);
  let result: EligibleOffer[];
  if (fitting.length > 0) {
    result = fitting;
  } else if (args.isBeforeSecondShipment) {
    // Preferred lever(s) missing. Pre-2nd cannot cancel — keep a closest attempt.
    result = [...args.eligible];
  } else if (
    args.subReason != null &&
    SKIP_SAVE_WHEN_NO_FITTING_LEVER.has(args.subReason)
  ) {
    result = [];
  } else if (args.offerDeclined) {
    // Confirm is already a real exit — do not invent a mismatched lever.
    result = [];
  } else {
    result = [...args.eligible];
  }

  // Pre-2nd exit is never "out of fit" — always keep it on the table when
  // eligible, but always LAST (it is the cancel substitute, not a first pitch).
  if (
    args.isBeforeSecondShipment &&
    args.eligible.includes("switch_to_one_time")
  ) {
    result = [
      ...result.filter((o) => o !== "switch_to_one_time"),
      "switch_to_one_time",
    ];
  }

  return result;
}

/**
 * Explicit idea decline (chip Not now / decline_* tool) always exhausts that
 * lever. The 2nd delay/freq presentation slot is only for a terms-adjusted
 * re-offer_* after { replied: true } without decline — not after they rejected
 * the idea. Leaving a declined lever on the menu blocks confirm_cancel (gated
 * on an empty menu) and re-pitches offers they already refused.
 */
export function shouldExhaustOfferOnDecline(args: {
  declined: EligibleOffer;
  availableOffers: readonly EligibleOffer[];
}): boolean {
  void args;
  return true;
}

/**
 * Pre-2nd exit gating for switch-to-one-time.
 *
 * Unlike other levers, switch must stay callable after a voice reply so the
 * model can re-present it when it understands the customer is insisting on
 * cancelling. Drop it only while an interactive card is pending, or after an
 * explicit decline.
 */
export function isPreSecondSwitchStillOfferable(args: {
  cards: readonly { kind: string; status: string; answer?: string | null }[];
}): boolean {
  const switchCards = args.cards.filter((c) => c.kind === "switch_to_one_time");
  if (switchCards.some((c) => c.status === "declined")) return false;
  if (switchCards.some((c) => c.status === "pending" && !c.answer)) {
    return false;
  }
  return true;
}

/** True once any switch-to-one-time card has been presented this conversation. */
export function hasSwitchBeenShown(
  cards: readonly { kind: string }[],
): boolean {
  return cards.some((c) => c.kind === "switch_to_one_time");
}

/** Lever the customer explicitly asked to revisit after switch was shown. */
export type ExplicitRetentionAsk = "delay" | "change_frequency";

/**
 * Detect a clear ask to delay or space out. Cancel / fee-refusal phrasing
 * returns null so switch-only mode stays intact.
 */
export function detectExplicitRetentionAsk(
  text: string,
): ExplicitRetentionAsk | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;

  const wantsDelay =
    /\bdelay\b/.test(t) ||
    /\bpause\b/.test(t) ||
    /\bpush\s+(it\s+)?back\b/.test(t) ||
    /\bskip\s+(this|my|the)\s+(month|delivery|order|shipment)\b/.test(t) ||
    (/\blater\b/.test(t) && /\b(delivery|ship|send|order)\b/.test(t));

  const wantsFreq =
    /\bspace\s+(it\s+|them\s+|deliveries\s+)?out\b/.test(t) ||
    /\bless\s+often\b/.test(t) ||
    /\bevery\s+\d+\s+months?\b/.test(t) ||
    /\bchange\s+(my\s+)?(frequency|cadence|schedule)\b/.test(t) ||
    /\bslow\s+down\b/.test(t);

  // Fee refusal / cancel insistence without a retention ask → stay on switch.
  if (
    !wantsDelay &&
    !wantsFreq &&
    /\b(cancel|don'?t want to pay|do not want to pay|just cancel|stop (my )?sub)\b/.test(
      t,
    )
  ) {
    return null;
  }

  if (wantsDelay && !wantsFreq) return "delay";
  if (wantsFreq && !wantsDelay) return "change_frequency";
  if (wantsDelay && wantsFreq) return "delay";
  return null;
}

/**
 * Option A — once switch has been shown on pre-2nd, the menu is switch-only
 * unless the customer explicitly asks to delay / space out (re-opens that lever
 * alongside switch as the exit).
 * Returns `null` when the normal menu should be used.
 */
export function resolveOffersAfterSwitchShown(args: {
  isBeforeSecondShipment: boolean;
  switchShown: boolean;
  switchStillOfferable: boolean;
  /** Explicit ask to revisit a retention lever after switch was shown. */
  explicitAsk?: ExplicitRetentionAsk | null;
  canOfferDelay?: boolean;
  canOfferChangeFrequency?: boolean;
}): EligibleOffer[] | null {
  if (!args.isBeforeSecondShipment || !args.switchShown) return null;

  const switchPart: EligibleOffer[] = args.switchStillOfferable
    ? ["switch_to_one_time"]
    : [];

  if (args.explicitAsk === "delay" && args.canOfferDelay) {
    return ["delay", ...switchPart];
  }
  if (
    args.explicitAsk === "change_frequency" &&
    args.canOfferChangeFrequency
  ) {
    return ["change_frequency", ...switchPart];
  }

  if (!args.switchStillOfferable) return [];
  return ["switch_to_one_time"];
}

function finite(n: number | null | undefined): number | null {
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

export function buildCancelChatContext(args: {
  product: ManageSubscriptionSheetProduct;
  isBeforeSecondShipment: boolean;
  isFlexible: boolean;
  /** Whether the change-frequency offer/dialog is wired (interval is readable). */
  changeFrequencyAvailable: boolean;
  /** Customer's first name, for natural (sparing) use in the conversation. */
  customerFirstName?: string | null;
  /** Titles of the OTHER products on this subscription contract. */
  siblingProductTitles?: string[];
  /** Qualifying swap candidates from the swap-offer endpoint. */
  swapCandidates?: { id: number; title: string; price: number }[];
  /** Every product line on THIS subscription contract (including `product`). */
  contractLines?: ManageSubscriptionSheetProduct[];
  /**
   * Whether the widget has computed a qualifying size-up bundle for this customer
   * (Rose Nectar, single-line, a price root, cost/month strictly lower). Only then
   * does `bundle` enter the menu; the card's numbers are computed at render from
   * the same SizeUpBundle. See cancel-chat-bundle.ts.
   */
  bundleAvailable?: boolean;
  /**
   * When the customer has scoped the chat to ONE line of a multi-line contract,
   * the line being removed and the lines that keep shipping. Rescopes the context
   * to that line and marks it a partial removal for the server prompt. Omit/null
   * on the whole-contract flow.
   */
  singleLineScope?: { removedTitle: string; retainedTitles: string[] } | null;
  /** The cancel-after-next-delivery switch (env or ?so_cancel_after_delivery=1). */
  cancelAfterDeliveryEnabled?: boolean;
}): CancelChatContext {
  const { product, isBeforeSecondShipment, isFlexible } = args;

  // Switch-to-one-time (and the fee it quotes) follows the conversation scope:
  //   - whole-contract → Stay AI converts the subscriptionId, so quote EVERY
  //     line's net + one-time totals (same figures the manage sheet shows);
  //   - single-line scope → only the chosen line is in play, so stay line-scoped.
  // Always go through lineTotals so quantity is counted the same way as the sheet.
  const linesForTotals = args.contractLines?.length
    ? args.contractLines
    : [product];
  const pricingLines = args.singleLineScope ? [product] : linesForTotals;
  const paidAmount = contractPerDeliveryTotal(pricingLines);
  const oneTimeAmount = contractOneTimeTotal(pricingLines);
  const estimatedDiff =
    paidAmount != null && oneTimeAmount != null
      ? Math.max(0, oneTimeAmount - paidAmount)
      : null;

  const contractTotal = contractPerDeliveryTotal(linesForTotals);

  const allowedReasons = getDisplayReasons(
    false,
    product.subscription_order_count,
    isFlexible,
  ).map((r) => ({ id: r.id, label: r.label }));

  const delayPresets = getDelayPresets(
    product.subscription_next_billing_date,
    product.subscription_last_charge_date,
  );
  const delayAvailable = delayPresets.length > 0;
  const changeFrequencyAvailable = args.changeFrequencyAvailable;

  // The eligible lever set — AVAILABILITY ONLY. The customer's REASON does not
  // gate what exists here; every reason sees the same levers, and the model picks
  // the one that answers what they actually said. The prompt carries the steering
  // ("price → lead with less-often", "don't like it → swap"), as advice weighed
  // against the conversation rather than a gate applied behind the model's back.
  //
  // What remains are gates on the customer's STATE, not their reason — things that
  // are genuinely impossible rather than editorially discouraged:
  //  - Before the 2nd shipment the customer CANNOT cancel. Their only exit is
  //    switch_to_one_time, which is not a retention offer at all: subscription
  //    pricing is discounted on the assumption of 2+ orders, so leaving early means
  //    paying back the difference (a FEE). Offered even to flexible subs so they're
  //    never trapped; no swap, and confirm-cancel is withheld entirely
  //    (see CancelSubscriptionChat).
  //  - swap is dropped per-turn without a valid bundle (see `remainingOffers`).
  const levers: EligibleOffer[] = [
    ...(delayAvailable ? (["delay"] as const) : []),
    ...(changeFrequencyAvailable ? (["change_frequency"] as const) : []),
  ];
  const stateOffers: EligibleOffer[] = isBeforeSecondShipment
    ? // The exit goes LAST: you do not lead with the fee. Where converting the
    // purchase IS the natural answer ("didn't mean to subscribe"), the prompt
    // says so — that is guidance the model applies, not an order we impose.
    [...levers, "switch_to_one_time"]
    : [
      // The size-up bundle leads the price levers when the widget computed one
      // (it's only ever gated onto price roots per-turn — see remainingOffers in
      // CancelSubscriptionChat), so it sits at the front of the menu.
      ...(args.bundleAvailable ? (["bundle"] as const) : []),
      ...levers,
      "swap",
    ];

  const eligibleOffersByReason: Record<string, EligibleOffer[]> = {};
  for (const r of allowedReasons) {
    eligibleOffersByReason[r.id] = stateOffers;
  }

  // Cadence, normalized like the widget's frequency presets: an unexpected unit
  // yields null (the model simply knows less) rather than nonsense.
  const cadence = (() => {
    const count = product.subscription_billing_interval_count;
    const unitRaw = product.subscription_billing_interval;
    if (count == null || !Number.isFinite(count) || count < 1 || !unitRaw) {
      return null;
    }
    const lower = unitRaw.toLowerCase();
    return ["day", "week", "month", "year"].includes(lower)
      ? { interval: count, intervalUnit: lower }
      : null;
  })();

  const firstName = args.customerFirstName?.trim().slice(0, 80) || null;

  // Single-line scope: normalize the removed line + the lines that keep shipping.
  const scope = args.singleLineScope
    ? {
        removedTitle: args.singleLineScope.removedTitle.trim().slice(0, 300),
        retainedTitles: [
          ...new Set(
            args.singleLineScope.retainedTitles
              .map((t) => t.trim().slice(0, 300))
              .filter((t) => t.length > 0),
          ),
        ].slice(0, 20),
      }
    : null;

  // Dedupe: sibling variants of one product share a title. In single-line scope
  // the siblings are NOT being cancelled, so `siblingLineTitles` is emptied — the
  // whole-contract subject and the "affects the WHOLE subscription" warning must
  // not fire; the retained lines ride in `singleLineScope` instead, where the
  // prompt frames them as staying rather than going.
  const siblingLineTitles = scope
    ? []
    : [
        ...new Set(
          (args.siblingProductTitles ?? [])
            .map((t) => t.trim().slice(0, 300))
            .filter((t) => t.length > 0),
        ),
      ].slice(0, 20);

  return {
    // Scoped to the chosen line: the greeting, the subject, and everything the
    // server prompt derives must name the product they're actually dropping.
    productTitle: scope ? scope.removedTitle : product.title,
    isBeforeSecondShipment,
    isFlexible,
    currencySymbol: "$",
    paidAmount,
    oneTimeAmount,
    estimatedDiff,
    cancelAfterNextDeliveryAvailable:
      (args.cancelAfterDeliveryEnabled ?? false) &&
      (estimatedDiff ?? 0) > 0 &&
      !!product.subscription_next_billing_date,
    allowedReasons,
    delayPresets,
    changeFrequencyAvailable,
    eligibleOffersByReason,
    cadence,
    nextBillingDate: product.subscription_next_billing_date ?? null,
    orderCount: finite(product.subscription_order_count),
    customerFirstName: firstName,
    siblingLineTitles,
    swapCandidates: (args.swapCandidates ?? [])
      .map((c) => ({ ...c, title: c.title.trim().slice(0, 300) }))
      .filter((c) => c.title.length > 0 && c.price > 0)
      .slice(0, 12),
    contractPerDeliveryTotal: contractTotal,
    singleLineScope: scope,
  };
}

/** The chat tool names the widget executes locally (no server `execute`). */
export type CancelChatToolName =
  | "record_intake"
  | "delay_subscription"
  | "change_frequency";

/**
 * Bounds for the model-proposed option SETS (mirrored in the server tool schemas
 * — keep in sync with prompt.ts). The model authors the buttons now: 1-3 options
 * sized to what the customer said, rather than a fixed preset list plus a
 * bolted-on suggestion. Three is the ceiling because a fourth button is decision
 * paralysis, not choice.
 *
 * Model input is untrusted: everything out of bounds is discarded, and a set
 * with nothing left in it returns null so the card falls back to the standard
 * presets. A bad turn can only ever degrade to the old behaviour, never to an
 * empty card.
 */
export const MAX_PROPOSED_DELAY_WEEKS = 12;
export const MAX_PROPOSED_OPTIONS = 3;

function clampList(value: unknown, min: number, max: number): number[] | null {
  if (!Array.isArray(value)) return null;
  const kept = [
    ...new Set(
      value.filter(
        (v): v is number =>
          typeof v === "number" && Number.isInteger(v) && v >= min && v <= max,
      ),
    ),
  ]
    .sort((a, b) => a - b)
    .slice(0, MAX_PROPOSED_OPTIONS);
  return kept.length > 0 ? kept : null;
}

export function clampProposedDelayWeeksList(value: unknown): number[] | null {
  return clampList(value, 1, MAX_PROPOSED_DELAY_WEEKS);
}

export function clampProposedFrequencyMultipliersList(
  value: unknown,
): number[] | null {
  return clampList(value, 2, 4);
}
